import classNames from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { Button } from 'ui/Button';
import { Input } from 'ui/Input';
import Logo from 'ui/Logo';

import backgroundImage from 'assets/images/background.png';
import { loginRequest } from 'store/Auth/Auth.action';
import { IRootStore } from 'store/Root.reducer';

import { PAGES } from 'utils/link';
import { filterEmailInput, filterPasswordInput, validateEmailFormat } from 'utils/validation';

const validationSchema = Yup.object({
  email: Yup.string()
    .test('required', 'Please fill out this field.', (value) => {
      return value !== undefined && value !== null && value.trim().length > 0;
    })
    .test('email-format', 'Invalid email format.', (value) => {
      if (!value || value.trim().length === 0) return true;
      return validateEmailFormat(value);
    }),
  password: Yup.string().test('required', 'Please fill out this field.', (value) => {
    return value !== undefined && value !== null && value.length > 0;
  }),
});

export const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useSelector((state: IRootStore) => state.auth);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: (values) => {
      const trimmedEmail = values.email.trim();
      dispatch(loginRequest(trimmedEmail, values.password) as any);
    },
  });

  const { setFieldValue, setFieldTouched } = formik;
  const errorHandledRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !loading && user) {
      if (user.role === 'admin') {
        navigate(PAGES.ADMIN_USERS, { replace: true });
      } else if (user.role === 'user') {
        const hasSensors = (user.meta?.sensors_count ?? 0) > 0;
        if (hasSensors) {
          navigate(PAGES.DASHBOARD.replace(':id', user.id.toString()), { replace: true });
        } else {
          navigate(PAGES.WELCOME, { replace: true });
        }
      }
    }
  }, [isAuthenticated, loading, user, navigate]);

  useEffect(() => {
    const errorStatus = error?.status;
    if (errorStatus === 401 && !errorHandledRef.current) {
      setFieldTouched('password', true);
      errorHandledRef.current = true;
    } else if (errorStatus !== 401) {
      errorHandledRef.current = false;
    }
  }, [error?.status, setFieldTouched]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const filtered = filterEmailInput(e.target.value);
      setFieldValue('email', filtered, false);
    },
    [setFieldValue],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const filtered = filterPasswordInput(e.target.value);
      setFieldValue('password', filtered, false);
    },
    [setFieldValue],
  );

  return (
    <div className={classNames('login-container')}>
      <div className={classNames('login-form-section')}>
        <div className={classNames('form-wrapper')}>
          <div className={classNames('login-header')}>
            <Logo className={classNames('logo')} />
          </div>

          <div className={classNames('welcome-text')}>
            <div className={classNames('title-wrapper')}>
              <h1 className={classNames('title')}>Welcome to</h1>
              <h1 className={classNames('brand-name')}>Prime Oasis AI!</h1>
            </div>
            <p className={classNames('subtitle')}>
              Please sign-in to your account
              <br />
              and start the adventure
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className={classNames('form')}>
            <Input
              name="email"
              type="email"
              label="Email"
              placeholder="Email"
              value={formik.values.email}
              onChange={handleEmailChange}
              onBlur={formik.handleBlur}
              maxLength={256}
              error={formik.touched.email && formik.errors.email ? formik.errors.email : undefined}
            />

            <Input
              name="password"
              type="password"
              label="Password"
              placeholder="Password"
              value={formik.values.password}
              onChange={handlePasswordChange}
              onBlur={formik.handleBlur}
              maxLength={64}
              error={
                formik.touched.password && formik.errors.password
                  ? formik.errors.password
                  : error && error.status === 401
                    ? 'Invalid email or password.'
                    : undefined
              }
            />

            {error && error.status !== 401 && (
              <div style={{ color: 'red', marginBottom: '16px', fontSize: '14px' }}>
                {error.message}
              </div>
            )}
            <Button type="submit" fullWidth size="large" loading={loading}>
              Login
            </Button>
          </form>
        </div>
      </div>

      <div className={classNames('image-section')}>
        <img
          src={backgroundImage}
          alt="Tropical paradise"
          className={classNames('background-image')}
        />
      </div>
    </div>
  );
};
